<template>
  <q-layout view="hHh lpR fFf" style="min-height: unset; min-width: unset">
    <q-header elevated class="ctp00100-header-primary text-white">
      <div class="ctp00100-logo">
        <div class="logo-area" @click="linkToTop">
          <img src="../assets/panda_logo.svg" alt="" />
        </div>
      </div>
      <div v-if="dispFlg" class="col-7 header-btn-area q-ml-md">
        <HeaderMenuButton
          v-for="(link, index) in getLinks"
          :key="index"
          :link="link"
          @open-dialog="downloadDialogFlg = true"
          @open-sidebar="openSidebar"
          @delete-sidebar="ismasterMaintenance = false"
        />
      </div>
      <q-space />
      <OrderQuotableButton
        v-if="dispFlg"
        :search-result="false"
        class="q-mr-sm"
        @delete-sidebar="ismasterMaintenance = false"
      />
      <div class="account-btn-area">
        <q-btn v-if="dispFlg">
          <div class="account-btn">
            <q-icon class="account-icon" name="mdi-account" color="primary"></q-icon>
          </div>
          <q-menu
            transition-show="jump-down"
            transition-hide="jump-up"
            class="account-menu"
            style="width: 180px"
          >
            <q-list style="min-width: 120px">
              <q-item v-if="poType === 'PO_NORMAL'" clickable @click="linkToMemberInfo">
                <q-item-section>会員情報</q-item-section>
              </q-item>
              <q-item clickable @click="download">
                <q-item-section>操作マニュアル</q-item-section>
              </q-item>
              <q-separator />
              <q-item v-if="poType === 'PO_NORMAL'" clickable @click="logoutDialogFlg = true">
                <q-item-section>ログアウト</q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </div>
    </q-header>

    <q-page-container :class="'page-container ' + (ismasterMaintenance ? 'mst-mainte' : '')">
      <router-view />

      <!-- 管理者画面用サイドバー -->
      <div v-if="ismasterMaintenance" class="cmd00001-side-menu">
        <q-drawer
          :v-model="true"
          show-if-above
          :mini="miniState"
          :width="220"
          :breakpoint="500"
          bordered
          persistent
        >
          <q-scroll-area class="fit mastermaintenance-items">
            <MasterMaintenanceMenuList mode="side" />
          </q-scroll-area>
          <q-btn
            dense
            unelevated
            color="primary"
            icon="chevron_left"
            class="q-mini-drawer-hide absolute sidebar-btn"
            @click="clickDrawerBtn"
          ></q-btn>
        </q-drawer>
      </div>
    </q-page-container>
    <!-- ダウンロード失敗ダイアログ -->
    <AlertDialog
      :display="downloadDialogFlg"
      level="error"
      confirmation
      title="ダウンロード失敗"
      :text="['操作マニュアルをダウンロードできませんでした。']"
      @complete="downloadDialogFlg = false"
    />
    <!-- ログアウト確認ダイアログ -->
    <q-dialog v-model="logoutDialogFlg" persistent class="alertdialog-area">
      <q-card>
        <q-toolbar class="information">
          <q-avatar>
            <q-icon name="mdi-alert-circle" />
          </q-avatar>
          <q-toolbar-title class="text-weight-bold">確認</q-toolbar-title>
        </q-toolbar>
        <q-card-section>ログアウトしますか？</q-card-section>
        <q-card-actions align="right" class="text-primary">
          <q-btn color="primary" label="OK" href="/logout" />
          <q-btn
            outline
            label="キャンセル"
            style="color: var(--q-delete); margin-left: 12px"
            @click="logoutDialogFlg = false"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-layout>
</template>

<script setup lang="ts">
import { ref, provide, readonly } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Loading } from 'quasar';
import { storeToRefs } from 'pinia';
import saveAs from 'file-saver';
import HeaderMenuButton from '../components/button/HeaderMenuButton.vue';
import OrderQuotableButton from '../components/button/OrderQuotableButton.vue';
import AlertDialog from '../components/dialogs/AlertDialog.vue';
import loginUserStore from '../store/loginUserStore';
import http from '../common/axios-common';
import countOrderQuotableKey from '../common/order-quotable-key';
import ResultStatusCode from '../common/code/ResultStatusCode';
import DetailResultStatusCode from '../common/code/DetailResultStatusCode';
import HttpResponseStatusCode from '../common/code/HttpResponseStatusCode';
import MasterMaintenanceMenuList from '../components/list/MasterMaintenanceMenuList.vue';

interface Props {
  // ログイン有無(未ログイン:0、ログイン済み:1[デフォルト値])
  isLogin?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isLogin: '1',
});

const router = useRouter();

// ユーザーID
const userId = ref<string>('');

// 表示フラグ
const dispFlg = Number(props.isLogin);

// ロゴ遷移先
const logoPath: string = Number(props.isLogin) ? 'top' : 'login';

// 画面遷移先
const getLinks = ref<unknown[]>([]);

// ログアウトダイアログフラグ
const logoutDialogFlg = ref<boolean>(false);

// マニュアルダウンロード失敗フラグ
const downloadDialogFlg = ref<boolean>(false);

// 注文件数
const orderCnt = ref<number>(0);
provide('orderCnt', readonly(orderCnt));

// 問い合わせ件数
const quotableCnt = ref<number>(0);
provide('quotableCnt', readonly(quotableCnt));

// サイドバーを最小化する（trueで最小化）
const miniState = ref(true);

// サイドバーを画面に出すかどうか（マスメン画面の場合に出す）
const ismasterMaintenance = ref(false);

// 最初に開いた画面のパスを格納
const route = ref(useRoute().path);

// ページ再読込で開いた画面がマスメン画面の場合にサイドメニュー表示
if (route.value.indexOf('MasterMaintenance') !== -1) {
  ismasterMaintenance.value = true;
}

// パンチアウト種別
const roleType = ref('');
const poType = ref('');
provide('roleType', readonly(roleType));
provide('poType', readonly(poType));

// マニュアルダウンロード
const download = () => {
  // Loading Start
  Loading.show();
  // パラメータ設定
  const formData = new FormData();
  formData.append('manual_type', 'user');

  // ファイル存在チェック
  http
    .post('/ctp001001/existManualFile', formData)
    .then((response) => {
      switch (response.data.common.status) {
        // 結果ステータス：正常終了
        case ResultStatusCode.SUCCESS:
          switch (response.data.common.detail_status) {
            // 詳細結果ステータス：正常終了
            case DetailResultStatusCode.SUCCESS:
              // マニュアルダウンロード
              saveAs(response.data.path, response.data.fileName);
              break;
            // 詳細結果ステータス：正常終了（メッセージ）
            case DetailResultStatusCode.SUCCESS_MESSAGE:
              // ダイアログ表示
              downloadDialogFlg.value = true;
              break;
            default:
              break;
          }
          break;
        // 結果ステータス：異常終了
        case ResultStatusCode.ERROR:
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
          break;
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

// TOPへ戻るボタン押下時
const linkToTop = () => {
  ismasterMaintenance.value = false;
  router.push({ name: logoPath });
};

// 会員情報画面へ遷移
const linkToMemberInfo = () => {
  ismasterMaintenance.value = false;
  router.push({ name: 'memberInfo' });
};

// マスターメンテナンス画面を開いたときサイドバーを出す
const openSidebar = () => {
  ismasterMaintenance.value = true;
  miniState.value = true;
};

// ボタン押下でサイドバーが開閉
const clickDrawerBtn = () => {
  miniState.value = !miniState.value;
};

// ログイン情報取得
const getLoginUser = () => {
  const loginUser = loginUserStore();
  const { user_id, role, po_type } = storeToRefs(loginUser);
  http
    .post('/ctp001001/loginUser')
    .then((response) => {
      // 異常終了の場合
      if (response.data.common.status === ResultStatusCode.ERROR) {
        router.push({ name: 'error' });
      } else {
        // ログインユーザー情報をstoreに設定
        loginUser.setLoginUser(response.data.loginUser);
        // ユーザーID設定
        userId.value = user_id.value;
        roleType.value = role.value;
        poType.value = po_type.value;
        // ヘッダー設定
        if (role.value === 'ROLE_ADMIN') {
          // 管理者
          getLinks.value = [
            {
              name: '検索',
              icon: 'mdi-magnify',
              menu: [
                { title: 'キー検索', name: 'search', type: '0' },
                { title: '構造検索', name: 'search', type: '1' },
              ],
            },
            { name: 'カタログデータ', icon: 'mdi-bookshelf', link: 'catalogData' },
            { name: 'お問い合わせ', icon: 'mdi-email-outline', link: 'inquiry' },
            {
              name: '管理',
              icon: 'mdi-cog',
              menu: [
                { title: 'マスタメンテナンス', name: 'masterTop' },
                { title: '会員管理', name: 'members' },
                { title: '注文・見積依頼履歴', name: 'orderQuote' },
                { title: '操作マニュアル（管理者）', name: '' },
              ],
            },
          ];
        } else if (po_type.value === 'PO_ARIBA') {
          // Aribaパンチアウト
          getLinks.value = [
            {
              name: '検索',
              icon: 'mdi-magnify',
              menu: [
                { title: 'キー検索', name: 'search', type: '0' },
                { title: '構造検索', name: 'search', type: '1' },
              ],
            },
          ];
        } else if (po_type.value === 'PO_RE_ARIBA') {
          // 再パンチアウト：チェックアウト以外は表示しない
        } else {
          // 一般ユーザ
          getLinks.value = [
            {
              name: '検索',
              icon: 'mdi-magnify',
              menu: [
                { title: 'キー検索', name: 'search', type: '0' },
                { title: '構造検索', name: 'search', type: '1' },
              ],
            },
            { name: 'お問い合わせ', icon: 'mdi-email-outline', link: 'inquiry' },
          ];
        }
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    });
};

// 注文問い合わせ件数取得
const countOrderQuotable = () => {
  http
    .post('/ctp001001/orderQuotable')
    .then((response) => {
      if (response.data.common.status === ResultStatusCode.ERROR) {
        router.push({ name: 'error' });
      } else {
        orderCnt.value = response.data.orderQuotableItem.order_cnt;
        quotableCnt.value = response.data.orderQuotableItem.quotable_cnt;
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    });
};
provide(countOrderQuotableKey, countOrderQuotable);

// 初期表示処理
// ログインしている場合
if (dispFlg) {
  getLoginUser();
}
</script>
<style scoped>
.ctp00100-header-primary {
  background: linear-gradient(to bottom, var(--q-gradientStart), var(--q-gradientEnd));
  height: 50px;
  display: flex;
  align-items: center;
}
.ctp00100-logo {
  width: 192px;
  height: 100%;
  position: relative;
}

.ctp00100-logo:after {
  content: '';
  height: calc(100% - 14px);
  position: absolute;
  top: 0;
  right: 0;
  border-left: 1px solid var(--q-whiteBorder);
  margin: 7px 0;
}
.ctp00100-logo .logo-area {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 20px 0 22px;
  position: relative;
}

.ctp00100-logo > .logo-area:hover {
  background: rgba(255, 255, 255, 0.22);
  transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1),
    opacity 0.4s cubic-bezier(0.25, 0.8, 0.5, 1);
  cursor: pointer;
}

.account-btn-area {
  height: 100%;
  display: flex;
  align-items: center;
}

.account-btn-area .q-btn {
  height: 100%;
  width: 100%;
  padding: 0 16px;
}

.account-btn-area:hover {
  background: rgba(255, 255, 255, 0.22);
  transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1),
    opacity 0.4s cubic-bezier(0.25, 0.8, 0.5, 1);
  cursor: pointer;
}
</style>
<style>
.ctp00100-header-primary .account-btn {
  transform: scale(0.8);
}

.ctp00100-header-primary .q-btn .q-icon.account-icon {
  font-size: 36px;
}

.account-menu.q-menu {
  border-radius: 0;
  padding: 0 0px 5px 15px;
  left: unset !important;
  right: 0;
  box-shadow: none;
  background: none;
}

.account-menu .q-list {
  box-shadow: 0 1px 5px rgb(0 0 0 / 20%), 0 2px 2px rgb(0 0 0 / 14%),
    0 3px 1px -2px rgb(0 0 0 / 12%);
  background: var(--q-pureWhite);
  border-radius: 0;
  min-width: 180px;
}

.q-page-container.page-container > div {
  padding-bottom: 40px;
}

.account-btn-area .q-btn--actionable.q-btn--standard:before {
  box-shadow: none;
  padding: 0 12px;
}

.ctp00100-header-primary .account-btn {
  border-radius: 50%;
  background-color: var(--q-secondary);
  padding: 6px;
}

/* サイドバー用Style */

.cmd00001-side-menu .q-drawer {
  width: 220px !important;
  box-shadow: -10px 0px 10px rgb(0 0 0 / 5%) inset;
  transition: left 0.2s linear;
  position: fixed;
  top: 50px;
}

.cmd00001-side-menu .q-drawer.q-drawer--standard {
  left: 0;
}

.cmd00001-side-menu .q-drawer.q-drawer--mini {
  left: -220px;
}

.cmd00001-side-menu .q-drawer--standard button {
  background-color: var(--q-pureWhite) !important;
  border: 1px solid var(--q-lightColorBorder);
  color: var(--q-lightColorBorder) !important;
}

.cmd00001-side-menu .q-drawer .q-btn .q-icon {
  transform: scale(2.5);
}

.cmd00001-side-menu .q-drawer--mini .q-mini-drawer-hide {
  transform: rotate(180deg);
}

.cmd00001-side-menu .q-mini-drawer-hide.absolute,
.cmd00001-side-menu .q-drawer--mini .q-mini-drawer-hide.absolute {
  display: flex;
  top: 8px;
}

.cmd00001-side-menu .q-drawer--standard .q-mini-drawer-hide.absolute {
  right: 10px !important;
}

.cmd00001-side-menu .q-drawer--mini .q-mini-drawer-hide.absolute {
  right: -62px !important;
}

.cmd00001-side-menu .q-drawer-container .q-btn {
  border-radius: 3px;
  width: 38px;
  height: 38px;
}
</style>
<style>
.q-layout .q-page-container.page-container .cmd00001-side-menu {
  padding: 0;
}
</style>
